// FixProgress.jsx — v160.4.93
//
// Live-updating progress page for a tester's fix request. Subscribes to
// the WebSocket channel `fix-progress:<requestId>` and renders each state
// transition as a checkmark on a vertical timeline.
//
// On mount:
//   1. Hits /v1/dist/me/fix-requests/:id/progress for the initial state
//      and any timeline already accumulated.
//   2. Opens a WebSocket to /ws?token=<jwt> and subscribes to the channel.
//   3. As each `fix-progress` message arrives, appends to the timeline.
//
// Reconnect: the WS layer auto-reconnects on close. We always replay from
// the REST endpoint when the page mounts so even a dropped connection
// resumes correctly.

import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { fixProgress, API_BASE, getToken, installURL, cancelFixRequest } from '../lib/api.js';

const ALL_STATES_ORDERED = [
  { key: 'queued',           label: 'Queued',                  emoji: '⏳' },
  { key: 'claimed',          label: 'Claimed by fixer',        emoji: '🔧' },
  { key: 'started',          label: 'Fix attempt started',     emoji: '▶️' },
  { key: 'prompt_sent',      label: 'Sent to Claude',          emoji: '🧠' },
  { key: 'claude_responded', label: 'Claude returned a fix',   emoji: '✨' },
  { key: 'gates_passed',     label: 'Tests passed',            emoji: '🟢' },
  { key: 'building',         label: 'Archiving + signing',     emoji: '📦' },
  { key: 'distributed',      label: 'New build available',     emoji: '✅' },
];

const TERMINAL_OK = ['distributed', 'completed'];
const TERMINAL_FAIL = ['gates_failed', 'rejected', 'regressed', 'cancelled'];

export default function FixProgress() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('queued');
  const [timeline, setTimeline] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState('');
  const [outputBuildId, setOutputBuildId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const wsRef = useRef(null);

  async function handleCancel() {
    if (!confirm('Cancel this fix request? Your feedback items will be released so you can re-submit them later.')) return;
    setCancelling(true);
    try {
      await cancelFixRequest(requestId);
      navigate('/feedback');
    } catch (e) {
      alert(`Cancel failed: ${e.message}`);
      setCancelling(false);
    }
  }

  // Initial REST fetch + periodic poll fallback (WS may be blocked on
  // some networks). The polling fallback is gentle (every 8s) and stops
  // automatically once the request hits a terminal state.
  // v160.4.93b: only re-run on requestId change, not on every state
  // change — previously each WS-driven setState recreated the interval,
  // causing a redundant REST fetch on every milestone.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    let pollTimer;
    let alive = true;

    async function fetchOnce() {
      try {
        const r = await fixProgress(requestId);
        if (!alive) return;
        setState(r.state);
        setTimeline(r.timeline || []);
        setAttempt(r.attempt);
        if (r.attempt?.outputBuildId) setOutputBuildId(r.attempt.outputBuildId);
      } catch (e) {
        if (alive) setError(e.message);
      }
    }

    fetchOnce();
    pollTimer = setInterval(() => {
      const cur = stateRef.current;
      if (TERMINAL_OK.includes(cur) || TERMINAL_FAIL.includes(cur)) {
        clearInterval(pollTimer);
        return;
      }
      fetchOnce();
    }, 8000);

    return () => { alive = false; clearInterval(pollTimer); };
  }, [requestId]);

  // Live WebSocket
  // v160.4.93b: derive the WS URL absolutely against the backend origin.
  // v160.4.93c: real reconnect with exponential backoff. Previous version
  // claimed "auto-reconnect" in a comment but had no implementation —
  // any WS drop (network blip, server restart, sleep/wake) silently
  // degraded the page to 8s REST polling for the rest of the session.
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    let attempt = 0;
    let timeoutHandle = null;
    let alive = true;
    let wsLocal = null;

    function buildWsURL() {
      let wsBase;
      if (API_BASE) {
        // v160.4.95: strip trailing slash so we don't end up with `wss://host//ws`.
        wsBase = API_BASE.replace(/\/+$/, '').replace(/^http/, 'ws');
      } else {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host.replace(/^[^.]+\./, 'api.');
        wsBase = `${proto}//${host}`;
      }
      return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
    }

    function connect() {
      if (!alive) return;
      try {
        wsLocal = new WebSocket(buildWsURL());
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = wsLocal;

      wsLocal.onopen = () => {
        attempt = 0;
        wsLocal.send(JSON.stringify({ type: 'subscribe', channel: `fix-progress:${requestId}` }));
        // After subscribing, refetch the REST progress to catch up on
        // any state changes that may have fired during the reconnect window.
        fixProgress(requestId).then(r => {
          if (!alive) return;
          setState(r.state);
          setTimeline(r.timeline || []);
          setAttempt(r.attempt);
          if (r.attempt?.outputBuildId) setOutputBuildId(r.attempt.outputBuildId);
        }).catch(() => {});
      };
      wsLocal.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type !== 'fix-progress' || msg.requestId !== requestId) return;
          // v160.4.93d: 'retrying' is a pseudo-state that signals the
          // backend requeued the request after a failed attempt. Reset
          // visual state to 'queued' but record the retry as a milestone.
          if (msg.state === 'retrying') {
            setState('queued');
            setTimeline(prev => [...prev, {
              state: 'retrying',
              label: `Retry ${msg.attemptCount}/${msg.maxPerThread} — ${msg.previousFailure || 'previous attempt failed'}`,
              at: msg.timestamp
            }]);
            return;
          }
          setState(msg.state);
          setTimeline(prev => {
            if (prev.some(t => t.state === msg.state && t.at === msg.timestamp)) return prev;
            return [...prev, { state: msg.state, label: msg.stateLabel || msg.state, at: msg.timestamp }];
          });
          if (msg.outputBuildId) setOutputBuildId(msg.outputBuildId);
          if (msg.attempt) setAttempt(msg.attempt);
        } catch { /* malformed */ }
      };
      wsLocal.onclose = () => {
        if (!alive) return;
        // Don't reconnect if we already hit a terminal state.
        if (TERMINAL_OK.includes(stateRef.current) || TERMINAL_FAIL.includes(stateRef.current)) return;
        scheduleReconnect();
      };
      wsLocal.onerror = () => { /* triggers onclose */ };
    }

    function scheduleReconnect() {
      attempt++;
      const backoffMs = Math.min(30_000, 1000 * Math.pow(2, Math.min(attempt, 5)));
      timeoutHandle = setTimeout(connect, backoffMs);
    }

    connect();

    return () => {
      alive = false;
      if (timeoutHandle) clearTimeout(timeoutHandle);
      try { wsLocal?.close(); } catch {}
    };
  }, [requestId]);

  // Determine current vs done vs upcoming for each rung.
  const isDone = (key) => {
    const idx = ALL_STATES_ORDERED.findIndex(s => s.key === key);
    const curIdx = ALL_STATES_ORDERED.findIndex(s => s.key === state);
    return idx < curIdx || (TERMINAL_OK.includes(state) && idx <= curIdx);
  };
  const isCurrent = (key) => key === state;

  const failed = TERMINAL_FAIL.includes(state);
  const succeeded = TERMINAL_OK.includes(state);

  return (
    <div className="page" style={{maxWidth: 600, margin: '0 auto', padding: 20}}>
      <Link to="/" className="muted small">← Back to apps</Link>
      <h1 style={{marginTop: 16}}>Fix in progress</h1>
      <p className="muted" style={{marginBottom: 24}}>
        Claude is analyzing your reports and generating a patch. You can leave
        this page open or come back later — we'll notify you when it's done.
      </p>

      {error && <div className="error" style={{marginBottom: 16}}>{error}</div>}

      <div style={{
        background: 'var(--bg-elev)',
        borderRadius: 16,
        padding: '20px 24px',
        border: '1px solid var(--border)'
      }}>
        {/* v160.4.93d: retry banner — show if any prior attempts failed and were requeued */}
        {timeline.filter(t => t.state === 'retrying').map((t, i) => (
          <div key={`retry-${i}`} style={{
            background: 'rgba(245,158,11,0.1)',
            border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 13
          }}>
            <span style={{color: '#f59e0b', fontWeight: 600}}>🔄 {t.label}</span>
          </div>
        ))}
        {ALL_STATES_ORDERED.map((s, i) => {
          const done = isDone(s.key);
          const current = isCurrent(s.key);
          const upcoming = !done && !current;
          // If we've failed before reaching this rung, mark it as skipped.
          const failedBeforeThis = failed
            && ALL_STATES_ORDERED.findIndex(x => x.key === state) < i;

          return (
            <div key={s.key} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 0',
              borderBottom: i < ALL_STATES_ORDERED.length - 1 ? '1px solid var(--border-subtle, #2a2a35)' : 'none',
              opacity: upcoming || failedBeforeThis ? 0.4 : 1
            }}>
              <div style={{
                width: 28, height: 28,
                borderRadius: '50%',
                background: done ? 'rgba(16,185,129,0.2)'
                          : current ? 'rgba(108,92,231,0.2)'
                          : 'transparent',
                border: `2px solid ${done ? '#10b981'
                                   : current ? 'var(--accent)'
                                   : 'var(--border)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginRight: 12,
                fontSize: 14
              }}>
                {done ? '✓' : current ? <Spinner /> : ''}
              </div>
              <div style={{flex: 1}}>
                <div style={{
                  fontWeight: current ? 600 : 400,
                  color: 'var(--text)'
                }}>
                  {s.emoji} {s.label}
                </div>
                {current && (
                  <div className="small muted" style={{marginTop: 2}}>
                    {humanizeCurrent(state, attempt)}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Failure terminal states */}
        {failed && (
          <div style={{
            marginTop: 16, padding: 14, borderRadius: 10,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)'
          }}>
            <div style={{fontWeight: 600, color: '#ef4444', marginBottom: 4}}>
              ❌ Fix attempt didn't produce a working build
            </div>
            <div className="small muted">
              {attempt?.gateFailureReason || attempt?.rejectionReason
                || 'No further details available.'}
            </div>
            <div className="small muted" style={{marginTop: 8}}>
              Your reports remain on file — you can submit them to a future
              fix attempt or have a developer review them manually.
            </div>
          </div>
        )}

        {/* Success — install button */}
        {succeeded && outputBuildId && (
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 12,
            background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
            textAlign: 'center'
          }}>
            <div style={{fontWeight: 600, color: '#10b981', marginBottom: 8, fontSize: 16}}>
              🎉 Fix is ready to install
            </div>
            <a className="install-cta" href={installURL(outputBuildId)} style={{display: 'inline-block', marginTop: 8}}>
              Install the fixed build
            </a>
            <div className="small muted" style={{marginTop: 8}}>
              Tap above on your iPhone to install. The app will auto-launch
              after install completes.
            </div>
          </div>
        )}
      </div>

      {attempt?.diff && (attempt.diff.linesAdded || attempt.diff.linesDeleted) ? (
        <div style={{marginTop: 16, fontSize: 13, color: 'var(--muted)'}}>
          Patch: <b style={{color: 'var(--text)'}}>{attempt.diff.filesChanged}</b> file
          {attempt.diff.filesChanged !== 1 ? 's' : ''} ·
          <span style={{color: '#10b981'}}> +{attempt.diff.linesAdded}</span> ·
          <span style={{color: '#ef4444'}}> −{attempt.diff.linesDeleted}</span>
        </div>
      ) : null}

      {/* v160.4.93c: cancel button — visible only while non-terminal */}
      {!TERMINAL_OK.includes(state) && !TERMINAL_FAIL.includes(state) && (
        <div style={{marginTop: 24, textAlign: 'center'}}>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={{
              background: 'transparent', color: 'var(--text-muted)',
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid var(--border)', fontSize: 13,
              cursor: cancelling ? 'default' : 'pointer',
              opacity: cancelling ? 0.5 : 1
            }}>
            {cancelling ? 'Cancelling…' : 'Cancel this fix request'}
          </button>
          <div style={{fontSize: 11, color: 'var(--text-muted)', marginTop: 6}}>
            Your reports will be released so you can re-submit them later.
          </div>
        </div>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 12, height: 12, borderRadius: '50%',
      border: '2px solid var(--accent)',
      borderTopColor: 'transparent',
      display: 'inline-block',
      animation: 'spin .8s linear infinite'
    }} />
  );
}

function humanizeCurrent(state, attempt) {
  const m = {
    queued: 'Waiting for fixer to claim',
    claimed: 'Fixer is starting up',
    started: 'Fix attempt initialized',
    prompt_sent: 'Asking Claude to analyze + generate a patch',
    claude_responded: 'Validating Claude\'s response',
    gates_passed: 'Tests passed — preparing build',
    awaiting_approval: 'Patch ready, awaiting developer approval',
    building: 'xcodebuild archive + ad-hoc signing',
    distributed: 'New build is available',
  };
  return m[state] || '';
}
